import { useEffect, useState } from 'react';

import {
  Session,
  assertAccountMatch,
  rememberedAccountId,
  requestCode,
  requestMagicLink,
  signInHotel,
  storeAccountId,
  verifyCode,
} from '../auth';
import { go, goReplace } from '../App';
import { resolveAccount } from '../config';
import { Segmented } from '../ui';

// The pending SMS challenge survives a refresh, so the code-entry screen
// can be a route (canon: every screen has a URL). The challengeId is opaque
// and single-purpose; the code still only ever arrives by SMS.
const PENDING_KEY = 'iso.pendingSms';
interface PendingSms {
  challengeId: string;
  phone: string;
}
function readPending(): PendingSms | null {
  try {
    const raw = sessionStorage.getItem(PENDING_KEY);
    return raw ? (JSON.parse(raw) as PendingSms) : null;
  } catch {
    return null;
  }
}

// The entry shell: no rail, no top bar — an ink panel beside a plain form.
// The ink panel does the brand work so the form can be plain. For hotels the
// account id comes FIRST — pre-auth it selects pool and client through the
// runtime map. Its shape is checked here, never its existence: ids are derived
// at provisioning, so an operator-minted hotel is one this bundle has never
// heard of (B2), and the credential is what decides — a client-side "not
// found" both refused real tenants and answered the existence question before
// authentication. Suppliers sign in by phone alone: the server resolves the
// tenant from the number (canon 11 / DECISION-059), so the SMS tab shows no
// account-id field. One generic failure message, never per-field.
export function SignIn({ segments, onSignedIn }: { segments: string[]; onSignedIn: (s: Session) => void }) {
  const [accountId, setAccountId] = useState(rememberedAccountId());
  const [remember, setRemember] = useState(!!rememberedAccountId());
  const [mode, setMode] = useState<'password' | 'sms'>('password');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [linkSentTo, setLinkSentTo] = useState('');

  const [phone, setPhone] = useState('');
  const [code, setCode] = useState('');

  // /signin/code — the SMS-code entry screen, a route not a state flag.
  const pending = readPending();
  const codeView = segments[0] === 'signin' && segments[1] === 'code';
  useEffect(() => {
    if (codeView && !pending) goReplace('/signin');
    // Landing on the code route puts the form on the SMS tab it belongs to.
    if (codeView) setMode('sms');
  }, [codeView, pending]);

  const gate = (): boolean => {
    // Suppliers sign in by phone; the server resolves the tenant from the number,
    // so the SMS path needs no account id (canon 11 / DECISION-059, finding ①).
    if (mode === 'sms') return true;
    // Shape only. This gate also fronts the magic-link button below, and that
    // link is a minted hotel's FIRST sign-in — the operator console creates
    // them with no password at all (api/src/adapters/users.ts) — so a refusal
    // here closes the only door they have.
    if (!resolveAccount(accountId)) {
      setError('The account id is 15 digits, starting with 2.');
      return false;
    }
    storeAccountId(accountId, remember);
    return true;
  };

  const run = async (fn: () => Promise<void>) => {
    if (!gate()) return;
    setBusy(true);
    setError('');
    try {
      await fn();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  // Hotels typed an account id, so the token must match it; suppliers signed in
  // by phone with no account id to match against — the token's tenant is truth.
  const done = (s: Session) => onSignedIn(mode === 'sms' ? s : assertAccountMatch(s, accountId));

  return (
    <div className="signin-wrap">
      <aside className="signin-ink">
        <div className="logo">ISO</div>
        <div className="mid">
          <div className="eyebrow">Rhodes · August</div>
          <h2>Every experience your guests book, and every operator who delivers it.</h2>
          <p className="sub">One place for hotels and their suppliers. Requests answered in minutes, not days.</p>
          <div className="stat-tiles">
            <div className="stat-tile">
              <div className="v">6 min</div>
              <div className="l">median answer</div>
            </div>
            <div className="stat-tile">
              <div className="v">94%</div>
              <div className="l">inside the hour</div>
            </div>
          </div>
        </div>
        <p className="sub" style={{ fontSize: 'var(--text-sm)' }}>
          New supplier with an invitation? Open the link the hotel sent you — there is no password to create.
        </p>
      </aside>

      <div className="signin-form-side">
        <div className="signin">
          <h1>Sign in</h1>
          <p className="welcome">Welcome back. We will take you to your side of the platform.</p>

          {!linkSentTo && (
            <>
              {mode === 'password' && (
                <div className="field">
                  <label htmlFor="accountId">Account id</label>
                  <input
                    id="accountId"
                    inputMode="numeric"
                    maxLength={15}
                    placeholder="15 digits"
                    value={accountId}
                    onChange={(e) => setAccountId(e.target.value.replace(/\D/g, ''))}
                    className="mono"
                    style={{ letterSpacing: '0.08em' }}
                    required
                  />
                  <label style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 8, fontWeight: 400 }}>
                    <input type="checkbox" style={{ width: 'auto', height: 'auto' }} checked={remember} onChange={(e) => setRemember(e.target.checked)} />
                    Remember this account id on this machine
                  </label>
                </div>
              )}

              <Segmented
                value={mode}
                onChange={setMode}
                options={[
                  { value: 'password', label: 'Email & password' },
                  { value: 'sms', label: 'Phone & SMS code' },
                ]}
              />
            </>
          )}

          {linkSentTo ? (
            // The "Magic link sent" entry screen: the machine waits for the
            // inbox. Nothing here reveals whether the account exists.
            <div>
              <h2 style={{ fontSize: 'var(--text-2xl)', color: 'var(--text-primary)', marginBottom: 8 }}>Check your email</h2>
              <p className="welcome">
                If {linkSentTo} has an account here, a sign-in link is on its way. Opening it signs
                this browser in — it works for 15 minutes.
              </p>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', marginTop: 12 }}
                disabled={busy}
                onClick={() => run(async () => requestMagicLink(linkSentTo))}
              >
                Send it again
              </button>
              <button type="button" className="btn secondary" style={{ width: '100%', marginTop: 8 }} onClick={() => setLinkSentTo('')}>
                Back to sign in
              </button>
            </div>
          ) : mode === 'password' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(async () => done(await signInHotel(email, password)));
              }}
            >
              <div className="field">
                <label htmlFor="email">Email</label>
                <input id="email" type="email" autoComplete="username" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="field">
                <label htmlFor="password">Password</label>
                <input id="password" type="password" autoComplete="current-password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              <button className="btn lg" disabled={busy} style={{ width: '100%' }}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', marginTop: 8 }}
                disabled={busy}
                onClick={() => {
                  if (!email) {
                    setError('Enter your email first — the link goes there.');
                    return;
                  }
                  run(async () => {
                    await requestMagicLink(email);
                    setLinkSentTo(email);
                  });
                }}
              >
                Email me a sign-in link instead
              </button>
            </form>
          ) : codeView && pending ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(async () => {
                  const s = await verifyCode(pending.challengeId, code);
                  sessionStorage.removeItem(PENDING_KEY);
                  done(s);
                });
              }}
            >
              <div className="field">
                <label htmlFor="code">The code we sent to {pending.phone}</label>
                <input
                  id="code"
                  inputMode="numeric"
                  pattern="[0-9]{6}"
                  maxLength={6}
                  autoComplete="one-time-code"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                  required
                  className="mono"
                  style={{ fontSize: 'var(--text-2xl)', letterSpacing: '0.4em', textAlign: 'center', height: 52 }}
                />
              </div>
              <button className="btn lg" disabled={busy || code.length !== 6} style={{ width: '100%' }}>
                {busy ? 'Checking…' : 'Sign in'}
              </button>
              <button
                type="button"
                className="btn ghost"
                style={{ width: '100%', marginTop: 8 }}
                onClick={() =>
                  run(async () => {
                    const id = await requestCode(pending.phone);
                    sessionStorage.setItem(PENDING_KEY, JSON.stringify({ challengeId: id, phone: pending.phone }));
                  })
                }
              >
                Send it again
              </button>
            </form>
          ) : (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                run(async () => {
                  const id = await requestCode(phone);
                  sessionStorage.setItem(PENDING_KEY, JSON.stringify({ challengeId: id, phone }));
                  go('/signin/code');
                });
              }}
            >
              <div className="field">
                <label htmlFor="phone">Phone number</label>
                <input id="phone" type="tel" placeholder="+30 …" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value.replace(/\s/g, ''))} required />
                <p className="hint" style={{ marginTop: 6 }}>
                  Suppliers sign in with a 6-digit SMS code — no password exists for them.
                </p>
              </div>
              <button className="btn lg" disabled={busy} style={{ width: '100%' }}>
                {busy ? 'Sending…' : 'Send the code'}
              </button>
            </form>
          )}

          {error && <p className="error-line" role="alert">{error}</p>}
        </div>
      </div>
    </div>
  );
}
